import { message } from "antd";
import axios, { AxiosRequestConfig, AxiosError } from "axios";
import { isStructuredCartQuantityError } from "../Pages/Orders/AddOrder/utils/productRequiresConfiguration";
import { makeApiCall, createRequestKey, requestManager } from "../utils/requestManager";
import { formatForbiddenErrorMessage } from "../utils/apiErrorMessage";
import i18n from "../i18n";

// True while an explicit, client-initiated logout is in progress (set from the header / router).
// All alerts and 401-driven logout side-effects are suppressed while this is true.
const isLogoutInProgress = () =>
  localStorage.getItem("isLoggingOut") === "true" ||
  localStorage.getItem("silentLogout") === "true";

// Helper function to check if we're in production mode
const isProductionMode = (): boolean => {
  // Check multiple ways to determine production
  const nodeEnv = import.meta.env.VITE_NODE_ENV;
  const reactAppEnv = import.meta.env.VITE_ENV;
  
  // In built React apps, NODE_ENV should be 'production'
  // Also check if it's explicitly not 'development' or 'test'
  return nodeEnv === 'production' || 
         reactAppEnv === 'production' || 
         (nodeEnv !== 'development' && nodeEnv !== 'test' && nodeEnv !== undefined);
};

// Determine base URL based on REACT_APP_LOCAL environment variable
const baseUrlPortal = import.meta.env.VITE_NODE_ENV === 'development' 
  ? import.meta.env.VITE_MAPPER_URL 
  : import.meta.env.VITE_PROD_MAPPER_URL;
let paxios = axios.create({
  timeout: 90000, // 90 seconds default timeout for axios requests (to handle slow search APIs)
});

const getToken = () => localStorage.getItem("token");

// Current UI language, sent as the `Lang` header so the backend localizes responses.
export const getRequestLang = (): "ar" | "en" =>
  i18n.language?.startsWith("ar") ? "ar" : "en";

const isSessionSuperseded = (responseData: unknown): boolean => {
  if (!responseData || typeof responseData !== "object") return false;
  const data = responseData as Record<string, unknown>;
  const errors = data.errors;
  if (errors && typeof errors === "object" && !Array.isArray(errors)) {
    if ((errors as Record<string, unknown>).error === "session_superseded") return true;
  }
  return data.error === "session_superseded";
};

// Builds the toast text the response interceptor shows for a validation-style body
// ({ errors } / { status: "error" }). Returns "" when no message can be built -- in that
// case the interceptor stays silent, so callers must not assume a toast was displayed.
export const extractApiErrorMessage = (responseData: any): string => {
  let errorMessage = '';

  if (responseData?.errors) {
    if (typeof responseData.errors === 'object' && !Array.isArray(responseData.errors)) {
      // e.g. { emails: ["The emails has already been taken."] }
      errorMessage = Object.values(responseData.errors)
        .flat()
        .filter((msg) => typeof msg === 'string' && msg.trim())
        .join(', ');
    } else if (Array.isArray(responseData.errors)) {
      errorMessage = responseData.errors.flat().filter(Boolean).join(', ');
    } else if (typeof responseData.errors === 'string') {
      errorMessage = responseData.errors;
    }
  }

  if (!errorMessage && typeof responseData?.message === 'string' && responseData.message.trim()) {
    errorMessage = responseData.message.trim();
  }

  return errorMessage;
};

// True when the response interceptor already owns the user-facing feedback for this error --
// either it showed a toast, or it suppressed one on purpose. Components must gate on this
// before toasting themselves. Gating on the shape of `errors` instead leaves two divergences:
// silent failures ({ status: "error" } with no usable message) and duplicate toasts (403 /
// dev 404 / dev 500 bodies that carry no `errors` field, which the interceptor toasts anyway).
export const wasHandledByInterceptor = (error: any): boolean => {
  const status = error?.response?.status;
  if (!status) return false;

  // Auth endpoints are excluded from interceptor alerts so the component can render them inline.
  const url = error?.config?.url || error?.response?.config?.url || '';
  if (
    url.includes('/auth/login') ||
    url.includes('/auth/refresh') ||
    url.includes('/auth/logout')
  ) {
    return false;
  }

  const responseData = error.response?.data;

  // Blob bodies (streamed downloads) skip every generic toast; the caller reads the blob.
  if (responseData instanceof Blob) return false;

  if (responseData?.errors || responseData?.status === 'error') {
    // Cart quantity bounds errors are suppressed on purpose and handled inline by add-order.
    if (isStructuredCartQuantityError(responseData?.errors)) return true;
    return !!extractApiErrorMessage(responseData);
  }

  // 401 ends in refresh+retry or logout -- never a component toast.
  if (status === 401 || status === 403) return true;

  return (status === 404 || status === 500) && !isProductionMode();
};

export const errorCatch = () => {};

// Shared refresh promise: ensures only one refresh HTTP call is in-flight at a time.
// All concurrent callers (request interceptor, response interceptor, useTokenRefresh hook)
// receive the same promise and therefore the same result token.
let refreshPromise: Promise<string | null> | null = null;

const handleLogout = () => {
  // Prevent multiple logout calls
  if (localStorage.getItem("isLoggingOut") === "true") return;
  localStorage.setItem("isLoggingOut", "true");

  // Logout is always silent. Do not show "session expired" or similar alerts — the login
  // page itself is a clear signal that the session ended, and showing toast messages here
  // causes duplicate alerts when several in-flight requests 401 at the same time.
  localStorage.removeItem("token");
  localStorage.removeItem("refresh-token");
  localStorage.removeItem("socketUrl");

  // Cancel every in-flight request so their interceptor branches don't run after we clear the
  // token (which otherwise fires "No token found. Please log in." alerts).
  try {
    requestManager.cancelAllRequests();
  } catch {
    // noop
  }

  // Navigate immediately. Using href (not reload) avoids re-entering the app with partial state.
  // Do NOT clear the logout flags here — the Login page clears them on mount, which keeps
  // every pending interceptor / getToken callback silent until the navigation completes.
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

// Check if token is expired or about to expire (within 5 minutes)
const isTokenExpired = (token: string | null, bufferMinutes: number = 5): boolean => {
  if (!token) return true;
  
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const expirationTime = payload.exp * 1000;
    const bufferTime = bufferMinutes * 60 * 1000;
    return Date.now() > (expirationTime - bufferTime);
  } catch (error) {
    if (!isProductionMode()) {
      console.error("Error decoding token:", error);
    }
    return true;
  }
};

// Perform token refresh using JWT rotation (the same JWT serves as both access and refresh token).
// Uses a shared-promise gate: if a refresh is already in-flight, all callers piggyback on the
// same HTTP call instead of firing concurrent refreshes that race and blacklist each other.
export const performTokenRefresh = (): Promise<string | null> => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const currentToken = getToken();
    if (!currentToken) return null;

    try {
      const company = localStorage.getItem("company");

      // Use plain axios (not paxios) to avoid interceptor loop
      const response = await axios.post(
        `${baseUrlPortal}auth/refresh`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${currentToken}`,
            'Content-Type': 'application/json',
            ...(company && { 'company': company })
          }
        }
      );

      const data = response.data;
      const newAccessToken = data.access_token || data.accessToken || data.token;

      if (!isProductionMode()) {
        console.log("Refresh response:", { keys: Object.keys(data), hasToken: !!newAccessToken });
      }

      if (newAccessToken) {
        localStorage.setItem("token", newAccessToken);
        return newAccessToken;
      }
      return null;
    } catch (error) {
      const responseData = axios.isAxiosError(error) ? error.response?.data : undefined;
      if (isSessionSuperseded(responseData)) {
        handleLogout();
        return null;
      }
      if (!isProductionMode()) {
        console.error("Token refresh failed:", error);
      }
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// Request interceptor - proactively refresh token before it expires.
// Uses the shared-promise gate so concurrent requests all wait for the same refresh.
paxios.interceptors.request.use(
  async (config) => {
    const token = getToken();

    const isAuthEndpoint = config.url?.includes('/auth/login') || 
                          config.url?.includes('/auth/refresh') ||
                          config.url?.includes('/auth/logout');

    if (!isAuthEndpoint && token && isTokenExpired(token, 2)) {
      const newToken = await performTokenRefresh();
      if (newToken) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${newToken}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle 401 errors
paxios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.message === "canceled") return Promise.reject(error);
    // If a logout is in progress (explicit or session-expired), stop here: no alerts, no retries.
    if (isLogoutInProgress()) {
      return Promise.reject(error);
    }
    
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
    
    // Check if error has a response (even if code is ERR_NETWORK, if there's a response, handle it as API error)
    // Sometimes axios sets code to ERR_NETWORK even when there's a response (CORS, malformed response, etc.)
    // Check multiple possible locations for response
    const errorResponse = error.response || (error as any)?.response || (error as any)?.request?.response;
    const responseStatus = errorResponse?.status || 
                          (error as any)?.status || 
                          (error as any)?.response?.status ||
                          (error as any)?.request?.status;
    // Check if response exists OR if status code exists (status means there was a response)
    // Also check if error.response exists directly (most common case)
    const hasResponse = !!(error.response || errorResponse || responseStatus);
    
    // Debug logging in development
    if (!isProductionMode()) {
      const debugResponseData = errorResponse?.data || (error as any)?.data || (error as any)?.response?.data;
      console.log('Error details:', {
        hasResponse: hasResponse,
        hasErrorResponse: !!error.response,
        hasAnyResponse: !!(error as any)?.response,
        response: errorResponse,
        responseData: debugResponseData,
        code: error.code,
        message: error.message,
        status: responseStatus,
        errorResponseStatus: errorResponse?.status,
        anyStatus: (error as any)?.status,
        fullError: error
      });
    }
    
    if (hasResponse) {
      // Use the response from error.response or (error as any).response
      // Also check for status directly on error object
      const status = errorResponse?.status || (error as any)?.status || responseStatus;
      // Try multiple locations for response data
      const responseData = errorResponse?.data || 
                          (error as any)?.data || 
                          (error as any)?.response?.data ||
                          error.response?.data;
      
      // Streamed downloads use responseType: 'blob', so a JSON error body arrives as
      // a Blob that cannot be inspected synchronously here. The generic toasts below
      // would then replace the real backend message ("Nothing to download.", "Too many
      // files selected…") with a misleading one, so they are skipped and the caller
      // reads the blob itself. Token handling (401) still applies.
      const isBlobBody = responseData instanceof Blob;

      // Check if this is an auth endpoint (login/refresh/logout) - let the component handle errors.
      // /auth/logout is included so an expired token on logout never triggers refresh+retry+alerts.
      const isAuthEndpoint = originalRequest.url?.includes('/auth/login') ||
                            originalRequest.url?.includes('/auth/refresh') ||
                            originalRequest.url?.includes('/auth/logout');

      // Superseded session: another login invalidated this token — logout immediately, no refresh.
      if (status === 401 && !isAuthEndpoint && isSessionSuperseded(responseData)) {
        handleLogout();
        return Promise.reject(error);
      }

      // Check for validation errors first (regardless of status code)
      const hasValidationErrors = responseData?.errors || responseData?.status === "error";
        
      // Show validation errors for any status code (except auth endpoints - let component handle)
      if (hasValidationErrors && !isAuthEndpoint) {
        // Cart quantity bounds errors use a structured `errors` object with a `code` field.
        // Add-order screens handle these inline; skip the generic global toast.
        if (isStructuredCartQuantityError(responseData?.errors)) {
          return Promise.reject(error);
        }

        // 403 bodies often look like { status: "error", message: "Forbidden" } and
        // would otherwise toast the bare "Forbidden" label from extractApiErrorMessage.
        const errorMessage =
          status === 403
            ? formatForbiddenErrorMessage(responseData)
            : extractApiErrorMessage(responseData);

        // Show validation error if we have a message
        if (errorMessage) {
          // Keep the permission-aware 403 copy exact; other statuses may include the code in non-prod.
          if (status === 403) {
            message.error(errorMessage);
          } else if (!isProductionMode()) {
            message.error(`${errorMessage} (${status})`);
          } else {
            message.error(errorMessage);
          }
        }
        
        // Return early to prevent duplicate error handling
        return Promise.reject(error);
      }
      
      // Handle specific status codes only if no validation errors
      if (status === 401 && !originalRequest._retry) {
        if (isAuthEndpoint) {
          return Promise.reject(error);
        }

        const currentToken = getToken();
        if (!currentToken) {
          handleLogout();
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        try {
          // All concurrent 401s await the same shared refresh promise --
          // only one HTTP call to /auth/refresh is made.
          const newAccessToken = await performTokenRefresh();

          if (!newAccessToken) {
            throw new Error("Failed to refresh token");
          }

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          }
          return paxios(originalRequest);

        } catch (refreshError) {
          handleLogout();
          return Promise.reject(refreshError);
        }
      }
      else if (status === 401) {
        handleLogout();
      } else if (status === 403 && !isBlobBody) {
        // Replace bare "Forbidden" with the permission-aware sentence when possible.
        message.error(formatForbiddenErrorMessage(responseData));
      } else if ((status === 500 || status === 404) && !isBlobBody) {
        // Show generic 404/500 errors only in development/staging, not in production
        if (!isProductionMode()) {
          // Try to extract error message from various locations
          let errorMessage = '';
          
          // Priority 1: message field (most common in Laravel/Symfony responses)
          if (responseData?.message) {
            errorMessage = responseData.message;
          }
          // Priority 2: exception field (for Symfony/Laravel errors)
          else if (responseData?.exception) {
            errorMessage = responseData.exception;
          }
          // Priority 3: error field
          else if (responseData?.error) {
            errorMessage = typeof responseData.error === 'string' ? responseData.error : JSON.stringify(responseData.error);
          }
          // Priority 4: errors object
          else if (responseData?.errors) {
            if (typeof responseData.errors === 'object' && !Array.isArray(responseData.errors)) {
              errorMessage = Object.values(responseData.errors)
                .flat()
                .filter((msg) => typeof msg === 'string' && msg.trim())
                .join(', ');
            } else if (Array.isArray(responseData.errors)) {
              errorMessage = responseData.errors.flat().filter(Boolean).join(', ');
            } else {
              errorMessage = String(responseData.errors);
            }
          }
          
          // Fallback to default message
          if (!errorMessage) {
            errorMessage = status === 404 ? "Resource not found." : "Sorry, something went wrong.";
          }
          
          // Always show the message with status code when not in production
          message.error(`${errorMessage} (${status})`);
        }
        // In production, silently fail (no error message shown)
      }
    } else {
      // Handle network errors (no response object - CORS, connection issues, etc.)
      // BUT: Check one more time if there's actually a response we missed
      // Sometimes axios errors have response but it's not in the standard location
      const finalCheckResponse = error.response || 
                                 (error as any)?.response || 
                                 (error as any)?.request?.response;
      const finalCheckStatus = finalCheckResponse?.status || 
                               (error as any)?.status || 
                               (error as any)?.request?.status;
      
      // If we found a response/status now, handle it as API error
      if (finalCheckResponse || finalCheckStatus) {
        const status = finalCheckStatus;
        // Try multiple locations for response data
        const responseData = finalCheckResponse?.data || 
                            (error as any)?.data || 
                            (error as any)?.response?.data ||
                            error.response?.data;

        const isAuthEndpoint = originalRequest.url?.includes('/auth/login') ||
                              originalRequest.url?.includes('/auth/refresh') ||
                              originalRequest.url?.includes('/auth/logout');

        if (status === 401 && !isAuthEndpoint && isSessionSuperseded(responseData)) {
          handleLogout();
          return Promise.reject(error);
        }
        
        // Check for validation errors
        const hasValidationErrors = responseData?.errors || responseData?.status === "error";
        
        if (hasValidationErrors) {
          // Extract validation error messages (same rules as the response path above).
          const errorMessage = extractApiErrorMessage(responseData);
          
          if (errorMessage) {
            if (!isProductionMode()) {
              message.error(`${errorMessage} (${status})`);
            } else {
              message.error(errorMessage);
            }
          }
        } else if (status === 500 || status === 404) {
          // Show 404/500 errors in development/staging
          if (!isProductionMode()) {
            // Priority: message > exception > error > errors object
            let errorMessage = '';
            
            if (responseData?.message) {
              errorMessage = responseData.message;
            } else if (responseData?.exception) {
              errorMessage = responseData.exception;
            } else if (responseData?.error) {
              errorMessage = typeof responseData.error === 'string' ? responseData.error : JSON.stringify(responseData.error);
            } else if (responseData?.errors) {
              if (typeof responseData.errors === 'object' && !Array.isArray(responseData.errors)) {
                const errorValues = Object.values(responseData.errors).filter(msg => msg);
                errorMessage = errorValues.join(', ');
              } else if (Array.isArray(responseData.errors)) {
                errorMessage = responseData.errors.join(', ');
              } else {
                errorMessage = String(responseData.errors);
              }
            }
            
            if (!errorMessage) {
              errorMessage = status === 404 ? "Resource not found." : "Sorry, something went wrong.";
            }
            
            // Always show the message with status code in development/staging
            message.error(`${errorMessage} (${status})`);
          }
        } else if (status) {
          // Any other status code with response
          if (!isProductionMode()) {
            // Priority: message > exception > error
            let errorMessage = '';
            
            if (responseData?.message) {
              errorMessage = responseData.message;
            } else if (responseData?.exception) {
              errorMessage = responseData.exception;
            } else if (responseData?.error) {
              errorMessage = typeof responseData.error === 'string' ? responseData.error : JSON.stringify(responseData.error);
            } else {
              errorMessage = `Error ${status}`;
            }
            
            message.error(`${errorMessage} (${status})`);
          }
        }
    } else {
        // True network error - no response at all
        // Show error messages in development/staging
      if (!isProductionMode()) {
          console.error("Network error full object:", error);
          // Show network error message with error code
          const errorCode = error?.code || 'NETWORK_ERROR';
          const errorMsg = error?.message || "Network error. Please check your connection.";
          message.error(`${errorMsg} (${errorCode})`);
        }
        // In production, silently fail - don't show error message to user for network errors
      }
    }

    return Promise.reject(error);
  }
);

// paxios.interceptors.response.use(
//   function (response) {
//     return response;
//   },
//   function (error) {
//     if (401 === error?.response?.status) {
//       localStorage.removeItem("token");
//       window.location.reload();
//     }

//     if (500 === error?.response?.status) {
//       message.error("Sorry. something went wrong.");
//     } else {
//       // message.error('Sorry. something went wrong.');`
//     }
//   }
// );

export default class Api {
  static async getToken() {
    let token = getToken();
    if (!token) {
      // If a logout is in progress, never attempt to silently re-login with default creds.
      // This prevented the duplicate "Login failed" / "No token found" toasts that were
      // showing on /login right after clicking logout.
      if (isLogoutInProgress()) {
        return null;
      }
      if (!isProductionMode()) {
        console.warn("Token is missing. Attempting to log in...");
      }
      token = await this.loginAndFetchToken();
    }
    return token;
  }

  static async loginAndFetchToken(email?: string, password?: string) {
    try {
      const loginBody = {
        email: email || import.meta.env.VITE_DEFAULT_EMAIL || "",
        password: password || import.meta.env.VITE_DEFAULT_PASSWORD || "",
      };

      const response = await axios.post(
        `${baseUrlPortal}auth/login`,
        loginBody
      );

      const data = response.data;
      const token = data.access_token || data.accessToken || data.token;
      if (token) {
        localStorage.setItem("token", token);
        return token;
      } else {
        return null;
      }
    } catch (error) {
      if (!isLogoutInProgress()) {
        message.error("Login failed. Please check your credentials.");
      }
      return null;
    }
  }

  static async login(credentials: { email: string; password: string }) {
    try {
      const response = await axios.post(
        `${baseUrlPortal}auth/login`,
        credentials
      );

      const data = response.data;
      const token = data.access_token || data.accessToken || data.token;
      if (token) {
        localStorage.setItem("token", token);
        return data;
      } else {
        throw new Error("No token received");
      }
    } catch (error) {
      throw error;
    }
  }

  static async get(route: string, config?: AxiosRequestConfig) {
    return await this.execute(route, null, "get", config);
  }

  static async put(route: string, params?: any, config?: AxiosRequestConfig) {
    return await this.execute(route, params, "put", config);
  }

  static async post(route: string, params?: any, config?: AxiosRequestConfig) {
    return await this.execute(route, params, "post", config);
  }

  static async postForm(
    route: string,
    params?: any,
    config?: AxiosRequestConfig
  ) {
    return await this.execute(route, params, "postForm", config);
  }

  static async putForm(
    route: string,
    params?: any,
    config?: AxiosRequestConfig
  ) {
    return await this.execute(route, params, "putForm", config);
  }

  static async delete(
    route: string,
    params?: any,
    config?: AxiosRequestConfig
  ) {
    const token = await this.getToken();
    if (!token) {
      if (!isProductionMode()) {
        console.error("No token found");
      }
      if (!isLogoutInProgress()) {
        message.error("No token found. Please log in.");
      }
      return Promise.reject("No token found");
    }

    let requestUrl = `${baseUrlPortal}${route}`;
    let requestConfig: AxiosRequestConfig = {
      ...config,
      headers: {
        Authorization: `Bearer ${token}`,
        Lang: getRequestLang(),
        from: "WEB",
        ...config?.headers,
      },
    };

    // If params is a string or number, append it to the URL
    if (params && (typeof params === 'string' || typeof params === 'number')) {
      requestUrl = `${requestUrl}/${params}`;
    } else if (params && typeof params === 'object' && params.data) {
      // If params has a data property, use it as the request body
      requestConfig.data = params.data;
    } else if (params && typeof params === 'object') {
      // If params is an object without data property, use it as the request body
      requestConfig.data = params;
    }

    // Let the interceptor handle 401 errors and token refresh
    let result = await paxios.delete(requestUrl, requestConfig);
    return result?.data;
  }

  static async execute(
    route: string,
    params: any,
    verb: "post" | "get" | "delete" | "put" | "postForm" | "putForm",
    axiosConfig?: AxiosRequestConfig
  ) {
    // Check if React Query or another library already provided a signal
    const externalSignal = axiosConfig?.signal;
    
    // Create request key WITHOUT including the signal (to properly identify duplicate requests)
    const configWithoutSignal = axiosConfig ? { ...axiosConfig } : {};
    delete configWithoutSignal.signal;
    const requestKey = createRequestKey(`${verb.toUpperCase()}:${route}`, { params, config: configWithoutSignal });
    
    // Use longer timeout for long-running API calls (products filter and search endpoints)
    let timeoutMs = 60000; // Default 60 seconds
    if (route.includes('products/pos/filter')) {
      timeoutMs = 120000; // 2 minutes for products filter (reasonable timeout for large product catalogs)
    } else if (route.includes('customers/search') || route.includes('/search')) {
      timeoutMs = 90000; // 90 seconds for search requests (to handle 40s+ API response times)
    } else if (
      route.includes('cart/add-product') ||
      route.includes('cart/add-product-with-pricing') ||
      route.includes('cart/update-product') ||
      route.includes('cart/add-freetext-product') ||
      route.includes('cart/update-freetext-product')
    ) {
      timeoutMs = 2000000; // 20 minutes for add-product requests (to handle slow backend processing)
    } else if (route.includes('customers/') && route.includes('/cart/')) {
      timeoutMs = 180000; // 3 minutes for customer cart requests (to handle slow cart loading)
    }else if (route.includes('offers/')) {
      timeoutMs = 120000; // 2 minutes for customers search requests (to handle slow backend processing)
    } else if (route.startsWith('activities/') && route.endsWith('/create')) {
      timeoutMs = 120000;
    }
    
    // If external signal exists (e.g., from React Query), use it directly without requestManager
    if (externalSignal) {
      const token = await this.getToken();
      if (!token) {
        const isProduction = import.meta.env.MODE === 'production' || import.meta.env.VITE_ENV === 'production';
        if (!isProduction) {
          console.error("No token found");
        }
        if (!isLogoutInProgress()) {
          message.error("No token found. Please log in.");
        }
        return Promise.reject("No token found");
      }
      
      let request: string[] = [`${baseUrlPortal}${route}`];
      let config: AxiosRequestConfig = {
        ...axiosConfig,
        signal: externalSignal, // Use the external signal
        timeout: timeoutMs, // Apply the calculated timeout
        headers: axiosConfig?.headers
          ? {
              Authorization: `Bearer ${token}`,
              Lang: getRequestLang(),
              from: "WEB",
              ...axiosConfig?.headers,
            }
          : {
              Authorization: `Bearer ${token}`,
              Lang: getRequestLang(),
              from: "WEB",
            },
      };
      
      if (route === "login") {
        config = {
          headers: {
            "Content-Type": "multipart/form-data;",
          },
        };
      }

      if (params) request.push(params);
      //@ts-ignore
      let result = await paxios?.[verb](...request, config);
      return result?.data;
    }
    
    // Otherwise, use requestManager for automatic cancellation
    return makeApiCall(
      requestKey,
      async (signal) => {
        const token = await this.getToken();
        if (!token) {
          const isProduction = import.meta.env.MODE === 'production' || import.meta.env.VITE_ENV === 'production';
          if (!isProduction) {
            console.error("No token found");
          }
          if (!isLogoutInProgress()) {
            message.error("No token found. Please log in.");
          }
          return Promise.reject("No token found");
        }
        let request: string[] = [`${baseUrlPortal}${route}`];
        let config: AxiosRequestConfig = {
          ...axiosConfig,
          signal, // Use requestManager's signal
          headers: axiosConfig?.headers
            ? {
                Authorization: `Bearer ${token}`,
                Lang: getRequestLang(),
                from: "WEB",
                ...axiosConfig?.headers,
              }
            : {
                Authorization: `Bearer ${token}`,
                Lang: getRequestLang(),
                from: "WEB",
              },
        };
        
        if (route === "login") {
          config = {
            headers: {
              "Content-Type": "multipart/form-data;",
            },
          };
        }

        if (params) request.push(params);
        //@ts-ignore
        let result = await paxios?.[verb](...request, config);
        return result?.data;
      },
      { params, config: configWithoutSignal },
      timeoutMs
    );
  }
}
